"use client"

import { useAutoAnimate } from "@formkit/auto-animate/react"
import { useState } from "react"
import { queueEnableBulkDiscount } from "src/components/(checkout)/cart/api"
import { BulkDiscountProgress } from "src/components/(checkout)/cart/bulkDiscount/BulkDiscountProgress"
import { useCart } from "src/components/(checkout)/cart/state"
import { PrimaryButton } from "src/components/common/PrimaryButton"
import { PercentIcon } from "src/components/icons"
import { bulkDiscountActiveContextKey, countBulkDiscountableItems } from "src/data/products/bulkDiscount"
import { localizeText } from "src/localization/utils"

export const BulkDiscountSection = () => {
  const T = localizeText(text)
  const cart = useCart()
  const [animationParent] = useAutoAnimate()
  const enabled = !!cart?.context?.[bulkDiscountActiveContextKey]
  const [enabling, setEnabling] = useState(false)

  const itemsInCart = countBulkDiscountableItems(cart)

  const enable = async () => {
    setEnabling(true)
    await queueEnableBulkDiscount()
    setEnabling(false)
  }

  return (
    <section
      ref={animationParent}
      className="bg-brand-secondary-lighter flex flex-col gap-6 rounded-xl px-6 py-8 text-left"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xl leading-tight">{T.teaser}</h2>
        <div className="bg-brand-primary rounded-full p-2 text-white">
          <PercentIcon className="size-6" />
        </div>
      </div>

      <p className="text-subtle text-sm text-pretty">{T.format_explanation(itemsInCart)}</p>

      <BulkDiscountProgress itemsInCart={itemsInCart} />

      {enabled ? (
        <div className="text-success font-bold">{T.active}</div>
      ) : (
        <PrimaryButton color="white" size="small" onClick={enable} loading={enabling}>
          {T.activate}
        </PrimaryButton>
      )}

      <p className="text-subtle text-sm text-pretty">{T.description}</p>
    </section>
  )
}

const de = {
  teaser: "Hol dir jetzt bis zu 15% Rabatt auf deinen Warenkorb!",
  activate: "Rabatt aktivieren",
  active: "Der Rabatt ist aktiv.",
  format_explanation: (count: number): string => {
    switch (count) {
      case 0:
        return "Zwei Boxen und du erhältst auf beide 5% Rabatt."
      case 1:
        return "Eine weitere Box und du erhältst 5% Rabatt."
      case 2:
        return "Eine weitere Box und du erhältst 10% Rabatt."
      case 3:
        return "Eine weitere Box und du erhältst 15% Rabatt."
      default:
        return "Glückwunsch, du hast dir den maximalen Rabatt von 15% geholt."
    }
  },
  description:
    "Der Rabatt gilt für alle Deko-Boxen, die du in den Warenkorb legst, auch wenn du verschiedene Werkstücke kaufst. Nicht kombinierbar mit anderen Rabatten.",
}

const en = {
  teaser: "Get up to 15% discount now on your shopping cart!",
  activate: "Activate discount",
  active: "The discount is active.",
  format_explanation: (count: number) => {
    switch (count) {
      case 0:
        return "Two boxes and you get 5% discount on both."
      case 1:
        return "One more box and you get 5% discount."
      case 2:
        return "One more box and you get 10% discount."
      case 3:
        return "One more box and you get 15% discount."
      default:
        return "Congratulations, you have taken the maximum discount of 15%."
    }
  },
  description:
    "The discount applies to all decor boxes you add to your cart, even if you buy different items. Not combinable with other discounts.",
}

const text = { de, en }
